import { useGlobalSearch, type GlobalSearchResult } from '@/modules/admin-v2/hooks/useGlobalSearch';
import { useI18n } from '@/shared/i18n';
import { Building2, Search, UserCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const PATH_BY_KIND: Record<GlobalSearchResult['kind'], string> = {
  unit: '/admin/unidades',
  user: '/admin/residentes',
};

export function GlobalSearchBar() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const { results, loading } = useGlobalSearch(query);

  const grouped = useMemo(() => {
    const units = results.filter((r) => r.kind === 'unit');
    const users = results.filter((r) => r.kind === 'user');
    return { units, users };
  }, [results]);

  const flatResults = useMemo(
    () => [...grouped.units, ...grouped.users],
    [grouped.units, grouped.users],
  );

  useEffect(() => {
    setActiveIndex(0);
  }, [flatResults.length]);

  const handleSelect = useCallback(
    (item: GlobalSearchResult) => {
      // La lista destino abre filtrada por el resultado elegido (email es único).
      const search =
        item.kind === 'unit'
          ? item.entity.name || item.entity.number
          : item.entity.email || item.entity.name;
      navigate(`${PATH_BY_KIND[item.kind]}?search=${encodeURIComponent(search ?? '')}`);
      setOpen(false);
      setQuery('');
      inputRef.current?.blur();
    },
    [navigate],
  );

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const isCmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (isCmdK) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
        return;
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
        inputRef.current?.blur();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || flatResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % flatResults.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + flatResults.length) % flatResults.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = flatResults[activeIndex];
      if (item) handleSelect(item);
    }
  };

  const showDropdown = open && query.trim().length >= 2;

  return (
    <div ref={containerRef} className="relative w-full">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-tertiary pointer-events-none" />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onInputKeyDown}
        placeholder={t('menu.v2.search.placeholder')}
        className="w-full pl-9 pr-3 py-2 text-sm rounded-xl bg-surface-secondary border border-theme-light text-theme placeholder:text-theme-tertiary focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all"
      />

      {showDropdown && (
        <div className="absolute left-0 right-0 top-full mt-2 rounded-2xl border border-theme-light bg-theme shadow-lg shadow-black/5 dark:shadow-black/30 overflow-hidden z-50">
          {loading ? (
            <div className="py-6 px-4 text-center text-xs text-theme-tertiary">Buscando…</div>
          ) : flatResults.length === 0 ? (
            <div className="py-6 px-4 text-center text-xs text-theme-tertiary">Sin resultados para “{query}”</div>
          ) : (
            <div className="max-h-[420px] overflow-y-auto py-1">
              {grouped.units.length > 0 && (
                <ResultGroup
                  title="Unidades"
                  items={grouped.units}
                  activeIndex={activeIndex}
                  baseIndex={0}
                  onSelect={handleSelect}
                />
              )}
              {grouped.users.length > 0 && (
                <ResultGroup
                  title="Residentes"
                  items={grouped.users}
                  activeIndex={activeIndex}
                  baseIndex={grouped.units.length}
                  onSelect={handleSelect}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultGroup({
  title,
  items,
  activeIndex,
  baseIndex,
  onSelect,
}: {
  title: string;
  items: GlobalSearchResult[];
  activeIndex: number;
  baseIndex: number;
  onSelect: (item: GlobalSearchResult) => void;
}) {
  return (
    <div className="py-1">
      <div className="px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-theme-tertiary">{title}</div>
      {items.map((item, idx) => {
        const globalIdx = baseIndex + idx;
        const isActive = globalIdx === activeIndex;
        const Icon = item.kind === 'unit' ? Building2 : UserCircle;
        return (
          <button
            key={`${item.kind}-${item.id}`}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onSelect(item)}
            className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
              isActive ? 'bg-primary-500/10 dark:bg-primary-400/15' : 'hover:bg-surface-secondary'
            }`}
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
              item.kind === 'unit'
                ? 'bg-primary-600/20 text-primary-800 dark:bg-primary-500/30 dark:text-primary-100'
                : 'bg-blue-600/20 text-blue-800 dark:bg-blue-500/30 dark:text-blue-100'
            }`}>
              <Icon size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-theme leading-tight truncate">{item.label}</p>
              <p className="text-[11px] text-theme-tertiary leading-tight truncate">{item.sublabel}</p>
            </div>
          </button>
        );
      })}
    </div>
  );
}
